/**
 * NewProjectModal — create a new project.
 *
 * Phase 17 scope:
 *  - Required: name, methodology, priority, start date, end date
 *  - Optional: description, status, sponsor, scope, budget
 *  - Auto-generated: id, spent (0), completion (0), CPI/SPI (1.0), empty arrays
 *  - Skipped at creation: team members, phases, baselines, objectives,
 *    tasks (all editable from project detail page after creation)
 *
 * After successful creation, the user is navigated to the new project's
 * detail page. The store's addProject action handles both API and
 * localStorage modes via the existing optimistic-update pattern.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea, Label } from '@/components/ui/Input';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { Button } from '@/components/ui/Button';
import { MethodologyMiniAdvisor } from '@/components/project/MethodologyMiniAdvisor';
import { useProjectStore } from '@/store/useProjectStore';
import { toast } from '@/store/useToastStore';
import { useWorkspaceCurrency } from '@/lib/useWorkspaceCurrency';
import { useCanEdit } from '@/lib/useCanEdit';
import type {
  Project,
  ProjectStatus,
  ProjectPriority,
  Methodology,
  Portfolio,
  Program,
} from '@/types';

/** Sentinel option value for the inline "create new" rows. */
const NEW_CONTAINER = '__new__';

/** Generate a PORT-/PROG- id (6 uppercase alphanumerics), matching the
 *  dedicated New Portfolio / New Program modals. */
function generateContainerId(prefix: 'PORT-' | 'PROG-'): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = prefix;
  for (let i = 0; i < 6; i += 1) id += alphabet[Math.floor(Math.random() * alphabet.length)];
  return id;
}

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Generate a project ID like "PRJ-A3F2K9" — 6 uppercase alphanumerics.
 * 36^6 = ~2 billion combinations; collisions are vanishingly unlikely
 * within a single workspace. We don't query for existing IDs because
 * the database PK constraint will reject duplicates if they ever happen
 * (and our optimistic-update pattern surfaces the error).
 */
function generateProjectId(): string {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let id = 'PRJ-';
  for (let i = 0; i < 6; i += 1) {
    id += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return id;
}

/** Today as YYYY-MM-DD for default start date. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** N months from today as YYYY-MM-DD for default end date. */
function nMonthsFromToday(n: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
}

export function NewProjectModal({ isOpen, onClose }: NewProjectModalProps) {
  const navigate = useNavigate();
  const addProject = useProjectStore((s) => s.addProject);
  const portfolios = useProjectStore((s) => s.portfolios);
  const programs = useProjectStore((s) => s.programs);
  const addPortfolio = useProjectStore((s) => s.addPortfolio);
  const addProgram = useProjectStore((s) => s.addProgram);
  const projectSizeTiers = useProjectStore((s) => s.projectSizeTiers);
  const currency = useWorkspaceCurrency();
  const canEdit = useCanEdit();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [methodology, setMethodology] = useState<Methodology>('Waterfall');
  const [priority, setPriority] = useState<ProjectPriority>('medium');
  const [status, setStatus] = useState<ProjectStatus>('planning');
  const [startDate, setStartDate] = useState(todayIso());
  const [endDate, setEndDate] = useState(nMonthsFromToday(3));
  const [sponsor, setSponsor] = useState('');
  const [scope, setScope] = useState('');
  const [budget, setBudget] = useState<number>(0);
  const [portfolioId, setPortfolioId] = useState<string>(''); // '' = unallocated
  const [programId, setProgramId] = useState<string>(''); // '' = unallocated
  const [sizeTierId, setSizeTierId] = useState<string>(''); // '' = not sized
  const [submitting, setSubmitting] = useState(false);
  const [showAdvisor, setShowAdvisor] = useState(false);
  // Inline "create a portfolio/program without leaving this modal" (name-only
  // quick-create; full details editable later on the Portfolios/Programs page).
  const [creatingPortfolio, setCreatingPortfolio] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [creatingProgram, setCreatingProgram] = useState(false);
  const [newProgramName, setNewProgramName] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setDescription('');
    setMethodology('Waterfall');
    setPriority('medium');
    setStatus('planning');
    setStartDate(todayIso());
    setEndDate(nMonthsFromToday(3));
    setSponsor('');
    setScope('');
    setBudget(0);
    setPortfolioId('');
    setProgramId('');
    setSizeTierId('');
    setSubmitting(false);
    setShowAdvisor(false);
    setCreatingPortfolio(false);
    setNewPortfolioName('');
    setCreatingProgram(false);
    setNewProgramName('');
  }, [isOpen]);

  // Quick-create a portfolio inline, then select it. Name-only; other fields
  // default (editable later on the Portfolios page).
  const handleCreatePortfolio = () => {
    const name = newPortfolioName.trim();
    if (!name) return;
    const now = new Date().toISOString();
    const portfolio: Portfolio = {
      id: generateContainerId('PORT-'),
      name,
      description: '',
      manager: '',
      status: 'active',
      startDate: null,
      endDate: null,
      targetBudget: 0,
      createdAt: now,
      updatedAt: now,
    };
    addPortfolio(portfolio);
    setPortfolioId(portfolio.id);
    setCreatingPortfolio(false);
    setNewPortfolioName('');
    toast.success(`Portfolio "${name}" created`);
  };

  // Quick-create a program inline. Nests under the selected portfolio (if any)
  // so the hierarchy stays consistent.
  const handleCreateProgram = () => {
    const name = newProgramName.trim();
    if (!name) return;
    const now = new Date().toISOString();
    const program: Program = {
      id: generateContainerId('PROG-'),
      name,
      description: '',
      manager: '',
      status: 'active',
      startDate: null,
      endDate: null,
      targetBudget: 0,
      portfolioId: portfolioId || null,
      createdAt: now,
      updatedAt: now,
    };
    addProgram(program);
    setProgramId(program.id);
    setCreatingProgram(false);
    setNewProgramName('');
    toast.success(`Program "${name}" created`);
  };

  // Validation
  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= 100;
  const datesValid =
    startDate !== '' && endDate !== '' && endDate >= startDate;
  const budgetValid = budget >= 0 && !Number.isNaN(budget);
  const formValid = nameValid && datesValid && budgetValid && !submitting;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!formValid) return;
    setSubmitting(true);

    const newProject: Project = {
      id: generateProjectId(),
      name: trimmedName,
      description: description.trim(),
      status,
      health: 'healthy',
      priority,
      methodology,
      startDate,
      endDate,
      completion: 0,
      budget: budget,
      spent: 0,
      pm: '',
      sponsor: sponsor.trim() || undefined,
      portfolioId: portfolioId || null,
      programId: programId || null,
      sizeTierId: sizeTierId || null,
      team: [],
      scope: scope.trim(),
      objectives: [],
      phases: [],
      wbsTasks: [],
      risks: [],
      issues: [],
      changes: [],
      baselines: [],
      activeBaselineId: null,
      createdDate: new Date().toISOString(),
      modifiedDate: new Date().toISOString(),
    };

    // addProject handles both API and localStorage modes via optimistic update.
    // The local store updates synchronously; if the API call fails a toast appears.
    addProject(newProject);

    toast.success(`Project "${newProject.name}" created`);
    onClose();
    // Navigate to the new project's detail page
    navigate(`/projects/${newProject.id}`);
  };

  const footer = (
    <>
      <Button variant="ghost" onClick={onClose} disabled={submitting}>
        Cancel
      </Button>
      <Button
        variant="accent"
        onClick={handleSubmit}
        disabled={!formValid}
        isLoading={submitting}
      >
        Create project
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New project"
      size="lg"
      footer={footer}
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {/* Name (required) */}
        <div>
          <Label htmlFor="np-name" required>
            Project name
          </Label>
          <Input
            id="np-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. CRM Migration, Q3 Marketing Campaign"
            autoFocus
            maxLength={100}
            disabled={submitting}
          />
          {name.length > 0 && !nameValid && (
            <div className="text-xs text-danger-dark mt-1">
              Name must be 1–100 characters
            </div>
          )}
        </div>

        {/* Description */}
        <div>
          <Label htmlFor="np-description">Description</Label>
          <Textarea
            id="np-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What is this project about?"
            rows={2}
            disabled={submitting}
          />
        </div>

        {/* Methodology + Priority + Status */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="np-methodology" required>
              Methodology
            </Label>
            <Select
              id="np-methodology"
              value={methodology}
              onChange={(e) => setMethodology(e.target.value as Methodology)}
              disabled={submitting}
            >
              <option value="Waterfall">Waterfall</option>
              <option value="Agile">Agile</option>
              <option value="Hybrid">Hybrid</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="np-priority" required>
              Priority
            </Label>
            <Select
              id="np-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as ProjectPriority)}
              disabled={submitting}
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="np-status">Status</Label>
            <Select
              id="np-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ProjectStatus)}
              disabled={submitting}
            >
              <option value="planning">Planning</option>
              <option value="on-track">On track</option>
              <option value="at-risk">At risk</option>
              <option value="critical">Critical</option>
              <option value="on-hold">On hold</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </div>
        </div>

        {/* Methodology advisor (inline, deterministic) */}
        <div>
          <button
            type="button"
            className="text-xs font-medium text-brand-orange hover:underline disabled:opacity-50"
            onClick={() => setShowAdvisor((v) => !v)}
            disabled={submitting}
          >
            {showAdvisor
              ? 'Hide recommendation'
              : 'Not sure? Get a recommendation'}
          </button>
          {showAdvisor && (
            <MethodologyMiniAdvisor
              className="mt-2"
              onApply={(m) => {
                setMethodology(m);
                setShowAdvisor(false);
                toast.success(`Methodology set to ${m}`);
              }}
              onOpenFull={() => {
                onClose();
                navigate('/methodology');
              }}
            />
          )}
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="np-start" required>
              Start date
            </Label>
            <Input
              id="np-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div>
            <Label htmlFor="np-end" required>
              End date
            </Label>
            <Input
              id="np-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={startDate}
              disabled={submitting}
            />
            {endDate !== '' && startDate !== '' && endDate < startDate && (
              <div className="text-xs text-danger-dark mt-1">
                End date must be on or after start date
              </div>
            )}
          </div>
        </div>

        {/* Sponsor + Budget */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label htmlFor="np-sponsor">Sponsor</Label>
            <Input
              id="np-sponsor"
              value={sponsor}
              onChange={(e) => setSponsor(e.target.value)}
              placeholder="e.g. Jane Doe, CFO"
              disabled={submitting}
            />
          </div>
          <div>
            <Label htmlFor="np-budget">Budget ({currency})</Label>
            <MoneyInput
              id="np-budget"
              value={budget}
              onChange={setBudget}
              min={0}
              placeholder="0"
              disabled={submitting}
            />
            {!budgetValid && (
              <div className="text-xs text-danger-dark mt-1">
                Budget must be a non-negative number
              </div>
            )}
          </div>
        </div>

        {/* Scope */}
        <div>
          <Label htmlFor="np-scope">Scope</Label>
          <Textarea
            id="np-scope"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            placeholder="Brief scope statement (what's in / out of scope)"
            rows={2}
            disabled={submitting}
          />
        </div>

        {/* Portfolio assignment (optional) — with inline quick-create */}
        <div>
          <Label htmlFor="np-portfolio">Portfolio</Label>
          {creatingPortfolio ? (
            <div className="flex gap-2">
              <Input
                autoFocus
                value={newPortfolioName}
                onChange={(e) => setNewPortfolioName(e.target.value)}
                placeholder="New portfolio name"
                disabled={submitting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreatePortfolio();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleCreatePortfolio}
                disabled={submitting || !newPortfolioName.trim()}
              >
                Create
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCreatingPortfolio(false);
                  setNewPortfolioName('');
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Select
              id="np-portfolio"
              value={portfolioId}
              onChange={(e) => {
                if (e.target.value === NEW_CONTAINER) setCreatingPortfolio(true);
                else setPortfolioId(e.target.value);
              }}
              disabled={submitting}
            >
              <option value="">Unallocated</option>
              {portfolios.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              {canEdit && <option value={NEW_CONTAINER}>+ New portfolio…</option>}
            </Select>
          )}
        </div>

        {/* Program assignment (optional) — with inline quick-create */}
        <div>
          <Label htmlFor="np-program">Program</Label>
          {creatingProgram ? (
            <div className="flex gap-2">
              <Input
                autoFocus
                value={newProgramName}
                onChange={(e) => setNewProgramName(e.target.value)}
                placeholder="New program name"
                disabled={submitting}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleCreateProgram();
                  }
                }}
              />
              <Button
                type="button"
                variant="secondary"
                onClick={handleCreateProgram}
                disabled={submitting || !newProgramName.trim()}
              >
                Create
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setCreatingProgram(false);
                  setNewProgramName('');
                }}
                disabled={submitting}
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Select
              id="np-program"
              value={programId}
              onChange={(e) => {
                if (e.target.value === NEW_CONTAINER) setCreatingProgram(true);
                else setProgramId(e.target.value);
              }}
              disabled={submitting}
            >
              <option value="">Unallocated</option>
              {programs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              {canEdit && <option value={NEW_CONTAINER}>+ New program…</option>}
            </Select>
          )}
          {creatingProgram && portfolioId && (
            <p className="text-xs text-ink-tertiary mt-1">
              Will be nested under the selected portfolio.
            </p>
          )}
        </div>

        {/* Project size assignment (optional) */}
        <div>
          <Label htmlFor="np-size">Project size</Label>
          <Select
            id="np-size"
            value={sizeTierId}
            onChange={(e) => setSizeTierId(e.target.value)}
            disabled={submitting}
          >
            <option value="">Not sized</option>
            {projectSizeTiers
              .filter((t) => !t.isArchived)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
          </Select>
          {projectSizeTiers.filter((t) => !t.isArchived).length === 0 && (
            <p className="text-xs text-ink-tertiary mt-1">
              No sizes configured — set them in Settings.
            </p>
          )}
        </div>

        <p className="text-xs text-ink-tertiary pt-2 border-t border-line-light">
          You can add team members, tasks, risks, change requests, and baselines
          after creating the project.
        </p>
      </form>
    </Modal>
  );
}
